import { Component, computed, inject, OnInit, Pipe, PipeTransform, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { BehaviorSubject, combineLatest, Observable, of, switchMap } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { AppEvent, CoOrganiserDetail, EventRegistration } from './models';
import { EventService } from './event.service';
import { UserService } from '../global/userService/user-service';
import { ImageService } from '../global/images/image.service';
import { Force, ForceService } from '../forces';
import { AlertService } from '../global/alert/alert';

@Pipe({ name: 'visibleCoOrganisers', standalone: true })
export class VisibleCoOrganisersPipe implements PipeTransform {
    transform(details: CoOrganiserDetail[] | undefined): CoOrganiserDetail[] {
        return (details || []).filter((co) => !!co.displayName);
    }
}

@Component({
    standalone: false,
    selector: 'abs-event-detail',
    template: `
        @if (event$ | async; as event) {
            <abs-page-layout [pageTitle]="event.title">
                <div class="event-hero" [class.event-hero--no-image]="!event.imageId">
                    @if (event.imageId) {
                        <img [src]="imageService.url(event.imageId)" alt="Event banner" class="event-hero-img" />
                    }
                    <div class="event-hero-overlay">
                        <h1 class="event-hero-title">{{ event.title }}</h1>
                    </div>
                </div>

                <div class="event-key-info">
                    <div class="key-item">
                        <ion-icon name="calendar-outline"></ion-icon>
                        <span>
                            {{ event.startDate | date: 'MMM d, yyyy' }}
                            @if (event.endDate && event.endDate !== event.startDate) {
                                &nbsp;&ndash;&nbsp;{{ event.endDate | date: 'MMM d, yyyy' }}
                            }
                        </span>
                    </div>
                    <div class="key-item">
                        <ion-icon name="location-outline"></ion-icon>
                        <span>
                            @if (event.location.venueName) {
                                {{ event.location.venueName }}&nbsp;&mdash;&nbsp;
                            }
                            @if (event.location.city) {
                                {{ event.location.city }},&nbsp;
                            }
                            @if (event.location.state) {
                                {{ event.location.state }},&nbsp;
                            }
                            {{ event.location.countryCode }}
                        </span>
                    </div>
                    @if (event.ticketPrice != null && event.ticketPrice > 0) {
                        <div class="key-item">
                            <ion-icon name="pricetag-outline"></ion-icon>
                            <span>{{ event.ticketPrice }} {{ event.currency || '' }}</span>
                        </div>
                    }
                    @if (event.paymentInstructions) {
                        <div class="key-item">
                            <ion-icon name="card-outline"></ion-icon>
                            <span>{{ event.paymentInstructions }}</span>
                        </div>
                    }
                </div>

                @if (event.eventPackFileId) {
                    <div class="event-pack-link">
                        <a [href]="imageService.url(event.eventPackFileId)" target="_blank">
                            <ion-icon name="document-outline"></ion-icon>
                            {{ 'GLOBAL.EVENTS.DETAIL.EVENT_PACK' | translate }}
                        </a>
                    </div>
                }

                @if (event.description) {
                    <div class="event-description">
                        <p>{{ event.description }}</p>
                    </div>
                }

                <!-- Registration Section -->
                @if (event.publishStatus === 'published') {
                    <div class="registration-section">
                        @if (myRegistration$ | async; as reg) {
                            @if (reg.status === 'withdrawn') {
                                <ion-card color="medium" class="reg-card">
                                    <ion-card-content class="reg-card-body">
                                        <ion-icon name="close-circle-outline"></ion-icon>
                                        <div class="reg-status-text">
                                            {{ 'GLOBAL.EVENTS.REGISTRATION.WITHDRAWN' | translate }}
                                            @if (reg.withdrawnAt) {
                                                <span class="reg-status-date">{{ reg.withdrawnAt | date: 'mediumDate' }}</span>
                                            }
                                        </div>
                                    </ion-card-content>
                                </ion-card>
                                @if (registering()) {
                                    <ion-button expand="block" disabled>
                                        <ion-spinner name="crescent"></ion-spinner>
                                    </ion-button>
                                } @else {
                                    <ion-button expand="block" (click)="register(event)">
                                        <ion-icon name="person-add-outline" slot="start"></ion-icon>
                                        <span>{{ 'GLOBAL.EVENTS.REGISTER_BUTTON' | translate }}</span>
                                    </ion-button>
                                }
                            } @else {
                                <div class="registration-status">
                                    @switch (reg.status) {
                                        @case ('active') {
                                            <ion-card color="success" class="reg-card">
                                                <ion-card-content class="reg-card-body">
                                                    <ion-icon name="checkmark-circle"></ion-icon>
                                                    <div class="reg-status-text">
                                                        {{ 'GLOBAL.EVENTS.REGISTRATION.REGISTERED' | translate }}
                                                        @if (reg.registeredAt) {
                                                            <span class="reg-status-date">{{ reg.registeredAt | date: 'mediumDate' }}</span>
                                                        }
                                                    </div>
                                                </ion-card-content>
                                            </ion-card>
                                        }
                                        @case ('pending') {
                                            <ion-card color="warning" class="reg-card">
                                                <ion-card-content class="reg-card-body">
                                                    <ion-icon name="time-outline"></ion-icon>
                                                    <div class="reg-status-text">
                                                        {{ 'GLOBAL.EVENTS.REGISTRATION.PENDING_APPROVAL' | translate }}
                                                        @if (reg.registeredAt) {
                                                            <span class="reg-status-date">{{ reg.registeredAt | date: 'mediumDate' }}</span>
                                                        }
                                                    </div>
                                                </ion-card-content>
                                            </ion-card>
                                        }
                                        @case ('waitlist') {
                                            <ion-card color="tertiary" class="reg-card">
                                                <ion-card-content class="reg-card-body">
                                                    <ion-icon name="list-outline"></ion-icon>
                                                    <div class="reg-status-text">
                                                        {{ 'GLOBAL.EVENTS.REGISTRATION.ON_WAITLIST' | translate }}
                                                        @if (reg.registeredAt) {
                                                            <span class="reg-status-date">{{ reg.registeredAt | date: 'mediumDate' }}</span>
                                                        }
                                                    </div>
                                                </ion-card-content>
                                            </ion-card>
                                        }
                                    }

                                    <ion-button fill="clear" color="danger" size="small" (click)="withdrawRegistration(event, reg)">
                                        {{ 'GLOBAL.EVENTS.REGISTRATION.WITHDRAW' | translate }}
                                    </ion-button>
                                </div>
                            }
                        } @else {
                            @if (registering()) {
                                <ion-button expand="block" disabled>
                                    <ion-spinner name="crescent"></ion-spinner>
                                </ion-button>
                            } @else {
                                <ion-button expand="block" (click)="register(event)">
                                    <ion-icon name="person-add-outline" slot="start"></ion-icon>
                                    <span>{{ 'GLOBAL.EVENTS.REGISTER_BUTTON' | translate }}</span>
                                </ion-button>
                            }
                        }
                    </div>
                }

                <!-- List Submission Section (separate from registration) -->
                @if (myRegistration$ | async; as reg) {
                    @if (reg.status === 'active' && (event.listSlots?.length ?? 0) > 0) {
                        <div class="list-submission-section">
                            <h3 class="section-title">
                                <ion-icon name="document-text-outline"></ion-icon>
                                {{ 'GLOBAL.EVENTS.LIST_SUBMISSION.TITLE' | translate }}
                            </h3>
                            @for (submission of reg.listSubmissions; track submission.slotLabel) {
                                <div class="list-slot">
                                    <div class="list-slot-header">
                                        <span class="list-slot-label">{{ submission.slotLabel }}</span>
                                        @switch (submission.status) {
                                            @case ('submitted') {
                                                <ion-badge color="success">{{
                                                    'GLOBAL.EVENTS.LIST_SUBMISSION.SUBMITTED' | translate
                                                }}</ion-badge>
                                            }
                                            @case ('rejected') {
                                                <ion-badge color="danger">{{
                                                    'GLOBAL.EVENTS.LIST_SUBMISSION.REJECTED' | translate
                                                }}</ion-badge>
                                            }
                                            @case ('not_submitted') {
                                                <ion-badge color="medium">{{
                                                    'GLOBAL.EVENTS.LIST_SUBMISSION.NOT_SUBMITTED' | translate
                                                }}</ion-badge>
                                            }
                                        }
                                    </div>
                                    @if (submission.toNotes) {
                                        <p class="list-slot-notes">{{ submission.toNotes }}</p>
                                    }
                                    @if (submission.status === 'not_submitted' || submission.status === 'rejected') {
                                        <ion-button
                                            class="btn-sm"
                                            fill="outline"
                                            (click)="pickForceForSlot(event, reg, submission.slotLabel)"
                                        >
                                            <ion-icon name="document-attach-outline" slot="start"></ion-icon>
                                            <span>{{ 'GLOBAL.EVENTS.LIST_SUBMISSION.SUBMIT_LIST' | translate }}</span>
                                        </ion-button>
                                    }
                                    @if (submission.status === 'submitted' && submission.forceId) {
                                        <div class="list-slot-summary">
                                            <span class="list-slot-name">{{ submission.snapshotData?.name }}</span>
                                            @if (submission.snapshotData?.faction) {
                                                <span class="list-slot-faction">{{ submission.snapshotData.faction }}</span>
                                            }
                                            @if (submission.snapshotData?.cost != null) {
                                                <span class="list-slot-cost">{{ submission.snapshotData.cost }} pts</span>
                                            }
                                        </div>
                                        <div class="list-slot-actions">
                                            <ion-button class="btn-sm" fill="outline" [routerLink]="['../../forces', submission.forceId]">
                                                <ion-icon name="eye-outline" slot="start"></ion-icon>
                                                <span>{{ 'GLOBAL.EVENTS.LIST_SUBMISSION.VIEW_LIST' | translate }}</span>
                                            </ion-button>
                                            <ion-button
                                                class="btn-sm"
                                                fill="outline"
                                                color="danger"
                                                (click)="withdrawList(event, reg, submission.slotLabel)"
                                            >
                                                <ion-icon name="close-circle-outline" slot="start"></ion-icon>
                                                <span>{{ 'GLOBAL.EVENTS.LIST_SUBMISSION.WITHDRAW_LIST' | translate }}</span>
                                            </ion-button>
                                        </div>
                                    }
                                </div>
                            }
                        </div>
                    }
                }

                <div class="event-stats">
                    <div class="stat-card">
                        <span class="stat-label">{{ 'GLOBAL.EVENTS.DETAIL.CAPACITY' | translate }}</span>
                        <span class="stat-value">
                            {{ event.capacity }}&nbsp;{{ 'GLOBAL.EVENTS.DETAIL.PLAYERS' | translate }}
                            @if (event.waitlistEnabled) {
                                <small class="stat-badge">{{ 'GLOBAL.EVENTS.DETAIL.WAITLIST_ENABLED' | translate }}</small>
                            }
                        </span>
                    </div>
                    <div class="stat-card">
                        <span class="stat-label">{{ 'GLOBAL.EVENTS.DETAIL.ROUNDS' | translate }}</span>
                        <span class="stat-value">{{ event.numberOfRounds }}</span>
                    </div>
                    <div class="stat-card">
                        <span class="stat-label">{{ 'GLOBAL.EVENTS.DETAIL.PAIRING_MODE' | translate }}</span>
                        <span class="stat-value">{{ event.pairingMode }}</span>
                    </div>
                    <div class="stat-card">
                        <span class="stat-label">{{ 'GLOBAL.EVENTS.DETAIL.REGISTRATION' | translate }}</span>
                        <span class="stat-value">
                            {{
                                (event.registrationApprovalMode === 'auto'
                                    ? 'GLOBAL.EVENTS.DETAIL.AUTO_ACCEPT'
                                    : 'GLOBAL.EVENTS.DETAIL.TO_APPROVAL'
                                ) | translate
                            }}
                        </span>
                    </div>
                    @if (event.listSlots?.length > 0) {
                        <div class="stat-card stat-card--wide">
                            <span class="stat-label">{{ 'GLOBAL.EVENTS.DETAIL.LIST_SLOTS' | translate }}</span>
                            <span class="stat-value">
                                @for (slot of event.listSlots; track slot.label) {
                                    <span class="slot-entry">
                                        {{ slot.label }}
                                        @if (slot.pointsLimit) {
                                            &mdash; {{ slot.pointsLimit }}&nbsp;pts
                                        }
                                        @if (slot.required) {
                                            <small class="stat-badge">{{ 'GLOBAL.EVENTS.DETAIL.REQUIRED' | translate }}</small>
                                        }
                                    </span>
                                }
                            </span>
                        </div>
                    }
                </div>

                <!-- Organiser: Manage Registrations -->
                @if (canEdit$ | async) {
                    <div class="registrations-manage">
                        <ion-button expand="block" fill="outline" [routerLink]="['registrations']">
                            <ion-icon name="people-outline" slot="start"></ion-icon>
                            <span>{{ 'GLOBAL.EVENTS.REGISTRATION.MANAGE_TITLE' | translate }}</span>
                        </ion-button>
                    </div>
                }

                <ion-list class="event-secondary" lines="full">
                    @if (event.organiserDisplayName) {
                        <ion-item>
                            <ion-label>
                                <h3>{{ 'GLOBAL.EVENTS.DETAIL.ORGANISER' | translate }}</h3>
                                <p>{{ event.organiserDisplayName }}</p>
                            </ion-label>
                        </ion-item>
                    }
                    @let visibleCos = event.coOrganiserDetails | visibleCoOrganisers;
                    @if (visibleCos.length > 0) {
                        <ion-item>
                            <ion-label>
                                <h3>{{ 'GLOBAL.EVENTS.DETAIL.CO_ORGANISERS' | translate }}</h3>
                                @for (co of visibleCos; track co.userId) {
                                    <p>{{ co.displayName }}</p>
                                }
                            </ion-label>
                        </ion-item>
                    }
                    <ion-item>
                        <ion-label>
                            <h3>{{ 'GLOBAL.EVENTS.DETAIL.VISIBILITY' | translate }}</h3>
                            <p>{{ event.visibility }}</p>
                        </ion-label>
                    </ion-item>
                </ion-list>

                <div class="buttons">
                    @if (canEdit$ | async) {
                        <ion-button [routerLink]="['edit']">
                            {{ 'GLOBAL.EVENTS.EDIT_BUTTON' | translate }}
                        </ion-button>
                    }
                </div>
            </abs-page-layout>
        }
    `,
    styles: [
        `
            .event-hero {
                position: relative;
                width: 100%;
                height: 280px;
                overflow: hidden;
                margin-bottom: 0;
            }

            .event-hero--no-image {
                height: auto;
                min-height: 100px;
                background: var(--ion-color-dark, #222428);
            }

            .event-hero-img {
                width: 100%;
                height: 280px;
                object-fit: cover;
                display: block;
            }

            .event-hero-overlay {
                position: absolute;
                inset: 0;
                display: flex;
                align-items: flex-end;
                padding: 20px 16px;
                background: linear-gradient(0deg, rgba(0, 0, 0, 0.7) 0%, rgba(0, 0, 0, 0.15) 60%, transparent 100%);
            }

            .event-hero--no-image .event-hero-overlay {
                position: relative;
                background: none;
            }

            .event-hero-title {
                margin: 0;
                font-size: 1.5rem;
                font-weight: 700;
                line-height: 1.25;
                color: #fff;
                text-shadow: 0 1px 4px rgba(0, 0, 0, 0.4);
            }

            .event-key-info {
                display: flex;
                flex-direction: column;
                gap: 8px;
                padding: 16px;
                background: var(--ion-color-light, #f4f4f4);
                border-bottom: 1px solid var(--ion-color-light-shade, #d7d8da);
            }

            .key-item {
                display: flex;
                align-items: flex-start;
                gap: 10px;
                font-size: 0.95rem;
                line-height: 1.4;
            }

            .key-item ion-icon {
                font-size: 1.1rem;
                flex-shrink: 0;
                margin-top: 2px;
                color: var(--ion-color-medium, #92949c);
            }

            .event-description {
                padding: 16px;
                border-bottom: 1px solid var(--ion-color-light-shade, #d7d8da);
            }

            .event-description p {
                margin: 0;
                font-size: 0.95rem;
                line-height: 1.6;
                color: var(--ion-color-dark, #222428);
            }

            .event-stats {
                display: grid;
                grid-template-columns: 1fr 1fr;
                gap: 1px;
                background: var(--ion-color-light-shade, #d7d8da);
                border-bottom: 1px solid var(--ion-color-light-shade, #d7d8da);
            }

            .stat-card {
                display: flex;
                flex-direction: column;
                gap: 4px;
                padding: 14px;
                background: var(--ion-background-color, #fff);
            }

            .stat-card--wide {
                grid-column: 1 / -1;
            }

            .stat-label {
                font-size: 0.7rem;
                font-weight: 700;
                text-transform: uppercase;
                letter-spacing: 0.06em;
                color: var(--ion-color-medium, #92949c);
            }

            .stat-value {
                font-size: 0.95rem;
                display: flex;
                flex-wrap: wrap;
                align-items: center;
                gap: 6px;
            }

            .stat-badge {
                font-size: 0.7rem;
                padding: 2px 6px;
                border-radius: 4px;
                background: var(--ion-color-light-shade, #d7d8da);
                color: var(--ion-color-dark, #222428);
            }

            .slot-entry {
                display: flex;
                align-items: center;
                gap: 6px;
            }

            .event-secondary {
                margin-top: 0;
            }

            .buttons {
                padding: 16px;
                display: flex;
                flex-direction: column;
                gap: 8px;
            }

            .registration-section {
                padding: 16px;
                border-bottom: 1px solid var(--ion-color-light-shade, #d7d8da);
            }

            .reg-card {
                margin: 0;
            }

            .reg-card-body {
                display: flex;
                align-items: center;
                gap: 10px;
                font-weight: 600;
                font-size: 0.95rem;
                padding: 12px 14px;
            }

            .reg-card-body ion-icon {
                font-size: 1.2rem;
                flex-shrink: 0;
            }

            .registration-status {
                display: flex;
                flex-direction: column;
                gap: 12px;
            }

            .reg-status-text {
                display: flex;
                flex-direction: column;
                gap: 2px;
            }

            .reg-status-date {
                font-size: 0.75rem;
                font-weight: 400;
                opacity: 0.7;
            }

            .list-submission-section {
                padding: 16px;
                display: flex;
                flex-direction: column;
                gap: 10px;
                border-bottom: 1px solid var(--ion-color-light-shade, #d7d8da);
            }

            .list-submission-section .section-title {
                display: flex;
                align-items: center;
                gap: 8px;
            }

            .list-submission-section .section-title ion-icon {
                font-size: 1.2rem;
                color: var(--ion-color-medium, #92949c);
            }

            .list-slot {
                display: flex;
                flex-direction: column;
                gap: 6px;
                padding: 10px;
                background: var(--ion-color-light, #f4f4f4);
                border-radius: 6px;
            }

            .list-slot-header {
                display: flex;
                align-items: center;
                justify-content: space-between;
            }

            .list-slot-label {
                font-weight: 600;
                font-size: 0.9rem;
            }

            .list-slot-notes {
                margin: 0;
                font-size: 0.85rem;
                color: var(--ion-color-danger-shade, #cf3c4f);
                font-style: italic;
            }

            .list-slot-summary {
                display: flex;
                align-items: center;
                gap: 8px;
                flex-wrap: wrap;
                margin-bottom: 6px;
                font-size: 0.9rem;
            }

            .list-slot-name {
                font-weight: 600;
            }

            .list-slot-faction {
                color: var(--ion-color-medium, #92949c);
            }

            .list-slot-faction::before {
                content: '·';
                margin-right: 8px;
            }

            .list-slot-cost {
                color: var(--ion-color-medium, #92949c);
            }

            .list-slot-cost::before {
                content: '·';
                margin-right: 8px;
            }

            .list-slot-actions {
                display: flex;
                gap: 8px;
                flex-wrap: wrap;
            }

            .registrations-manage {
                padding: 16px;
                border-bottom: 1px solid var(--ion-color-light-shade, #d7d8da);
            }

            .section-title {
                margin: 0 0 12px;
                font-size: 1rem;
                font-weight: 700;
            }

            .force-picker-list {
                max-height: 50vh;
                overflow-y: auto;
            }
        `
    ]
})
export class EventDetailPage implements OnInit {
    private eventService = inject(EventService);
    private userService = inject(UserService);
    private forceService = inject(ForceService);
    private alertService = inject(AlertService);
    private route = inject(ActivatedRoute);

    imageService = inject(ImageService);

    event$: Observable<AppEvent>;
    canEdit$: Observable<boolean>;
    myRegistration$: Observable<EventRegistration | null>;

    registering = signal(false);

    private refreshRegistration$ = new BehaviorSubject<void>(undefined);

    ngOnInit() {
        this.event$ = this.route.paramMap.pipe(
            map((params) => params.get('eventId')),
            switchMap((id) => this.eventService.getEvent(id))
        );

        this.canEdit$ = combineLatest([this.event$, this.userService.login$]).pipe(
            map(([event, login]) => {
                const userId = login?.user?.userId;
                if (!userId) return false;

                const isOrganiser = event.organiserId === userId;
                const isCoOrganiser = event.coOrganisers?.includes(userId) || false;

                if (isCoOrganiser && !login?.user?.displayName) return false;

                return isOrganiser || isCoOrganiser;
            })
        );

        this.myRegistration$ = combineLatest([this.event$, this.userService.login$, this.refreshRegistration$]).pipe(
            switchMap(([event, login]) => {
                if (!login?.user?.userId || !event?._id) return of(null);
                return this.eventService.getMyRegistration(event._id).pipe(catchError(() => of(null)));
            })
        );
    }

    register(event: AppEvent) {
        const login = this.userService.loginDetails();
        if (!login?.user) return;

        this.registering.set(true);
        this.eventService
            .register(event._id, {
                displayName: login.user.displayName || login.user.email
            })
            .subscribe({
                next: () => {
                    this.registering.set(false);
                    this.refreshRegistration$.next();
                },
                error: () => {
                    this.registering.set(false);
                }
            });
    }

    withdrawRegistration(event: AppEvent, reg: EventRegistration) {
        this.eventService.updateRegistrationStatus(event._id, reg._id, 'withdrawn').subscribe(() => {
            this.refreshRegistration$.next();
        });
    }

    async pickForceForSlot(event: AppEvent, reg: EventRegistration, slotLabel: string) {
        const forces = this.forceService.forces() || [];
        if (forces.length === 0) {
            this.alertService.showAlert('', 'GLOBAL.EVENTS.REGISTRATION.NO_FORCES');
            return;
        }

        const slot = event.listSlots?.find((s) => s.label === slotLabel);
        const buttons = forces.map((force) => ({
            text: `${force.name} (${force.cost ?? 0} pts)`,
            handler: () => {
                this.submitForceToSlot(event, reg, slotLabel, force);
            }
        }));
        buttons.push({ text: 'Cancel', handler: () => {} });

        this.alertService.showAlert(slot?.label || slotLabel, 'GLOBAL.EVENTS.REGISTRATION.PICK_FORCE', buttons);
    }

    private submitForceToSlot(event: AppEvent, reg: EventRegistration, slotLabel: string, force: Force) {
        this.eventService
            .submitList(event._id, reg._id, {
                slotLabel,
                forceId: force.id,
                snapshotData: { name: force.name, faction: force.faction, cost: force.cost }
            })
            .subscribe(() => {
                this.refreshRegistration$.next();
            });
    }

    withdrawList(event: AppEvent, reg: EventRegistration, slotLabel: string) {
        this.eventService.withdrawList(event._id, reg._id, slotLabel).subscribe(() => {
            this.refreshRegistration$.next();
        });
    }
}
